import { WizardStep } from "@/Components/wizard/wizardStep";
import { Issue } from "@/Components/Issues/Issue";

interface BasicPageTourViewOnlyProps {
    viewOnly: boolean;
    issueContext?: {
        dialogOpen: boolean;
        setDialogOpen: (open: boolean) => void;
        issueTableDemoContent: Issue[];
        setIssueTableDemoContent: (content: Issue[]) => void;
        showPopup: (Issue: Issue) => void;
        rowSelection: {};
        setRowSelection: React.Dispatch<React.SetStateAction<{}>>;
    };
}

export function getBasicPageTourViewOnlySteps({ viewOnly, issueContext }: BasicPageTourViewOnlyProps): WizardStep[] {
    const steps: WizardStep[] = [
        {
            stepId: 1,
            stepName: "View Options",
            stepDescription: "Select the columns you want to view",
            isCompleted: false,
            stepComponentId: "viewOptionsButton",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
            sendClick: true,
        },
        {
            stepId: 2,
            stepName: "Global Search",
            stepDescription: "Use the global search to find specific data",
            isCompleted: false,
            stepComponentId: "globalSearch",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 3,
            stepName: "Reset Filters",
            stepDescription: "Reset all filters to view all data",
            isCompleted: false,
            stepComponentId: "resetFilters",
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 4,
            stepName: "Export Data",
            stepDescription: "Export the current data view to a CSV file",
            isCompleted: false,
            stepComponentId: "exportButton",
            displaySide: "left" as "right" | "left" | "top" | "bottom",
            sendClick: true,
        },
        {
            stepId: 5,
            stepName: "Column Dragging",
            stepDescription: "You can drag and drop the columns to reorder them by clicking and holding the grip icon",
            isCompleted: false,
            stepComponentId: "columnDraggable",
            useClassSelector: true,
            classSelectorNumber: 1,
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 6,
            stepName: "Column Sorting",
            stepDescription: "Choose how to sort the columns by clicking the column header",
            isCompleted: false,
            stepComponentId: "columnSortable",
            useClassSelector: true,
            classSelectorNumber: 1,
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 7,
            stepName: "Column Filters",
            stepDescription: "You can also filter the data in the columns",
            isCompleted: false,
            stepComponentId: "columnFilter",
            useClassSelector: true,
            classSelectorNumber: 1,
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        }];

    if (!viewOnly) {
        steps.push(
            {
                stepId: 7.1,
                stepName: "Select All Rows",
                stepDescription: "Use this checkbox to select all rows on the current page",
                isCompleted: false,
                stepComponentId: "table-select-all",
                displaySide: "right" as "right" | "left" | "top" | "bottom",
                functionCall: () => {
                    if (issueContext) { issueContext.setRowSelection({}); }
                },
            },
            {
                stepId: 7.2,
                stepName: "Select Row",
                stepDescription: "Use this checkbox to select a specific row",
                isCompleted: false,
                stepComponentId: "table-row-checkbox",
                useClassSelector: true,
                classSelectorNumber: 0,
                displaySide: "right" as "right" | "left" | "top" | "bottom",
                functionCall: () => {
                    if (issueContext) { issueContext.setRowSelection({ 0: true }); }
                },
            },
            {
                stepId: 7.3,
                stepName: "Group Actions",
                stepDescription: "Group actions offer a quicker way to manage multiple rows. With multiple rows selected you can perform group actions such as delete and setting status",
                isCompleted: false,
                stepComponentId: "table-group-actions",
                displaySide: "right" as "right" | "left" | "top" | "bottom",
                functionCall: () => {
                    if (issueContext) { issueContext.setRowSelection({ 0: true }); }
                },
                sendClick: true,
            },
            {
                stepId: 7.4,
                stepName: "Row Options",
                stepDescription: "Click here to view options for a single row such as edit or delete",
                isCompleted: false,
                stepComponentId: "table-row-menu-button",
                useClassSelector: true,
                classSelectorNumber: 0,
                functionCall: () => {
                    if (issueContext) { issueContext.setRowSelection({}); }
                },
                displaySide: "left" as "right" | "left" | "top" | "bottom",
            }
        );
    }

    steps.push(
        {
            stepId: 8,
            stepName: "Table Rows",
            stepDescription: "Rows only show the columns you have selected in the view options. Clicking on a row will open the details view for that Risk.",
            isCompleted: false,
            stepComponentId: "tableRow",
            useClassSelector: true,
            classSelectorNumber: 0,
            displaySide: "bottom" as "right" | "left" | "top" | "bottom",
            functionCall: () => { if (issueContext) { issueContext.setDialogOpen(false); } },
        },
        {
            stepId: 9,
            stepName: "Details View",
            stepDescription: "The details view shows all the information about the selected Risk",
            isCompleted: false,
            stepComponentId: "issue-dialog",
            showAboveDialog: true,
            functionCall: () => { if (issueContext) { issueContext.showPopup(issueContext.issueTableDemoContent[0]); } },
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 10,
            stepName: "Rows Per Page",
            stepDescription: "Choose how many rows to display per page",
            isCompleted: false,
            stepComponentId: "rowsPerPage",
            functionCall: () => { if (issueContext) { issueContext.setDialogOpen(false); } },
            displaySide: "top" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 11,
            stepName: "Page Number",
            stepDescription: "This shows the current page number and total pages",
            isCompleted: false,
            stepComponentId: "pageIndicator",
            displaySide: "top" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 12,
            stepName: "Pagination Controls",
            stepDescription: "Navigate through pages of data",
            isCompleted: false,
            stepComponentId: "pagination-controls",
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 13,
            stepName: "Refresh Data",
            stepDescription: "Click here to refresh the data in the table. This will also refresh the page lock.",
            isCompleted: false,
            stepComponentId: "refreshPageLock",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 14,
            stepName: "Page Lock",
            stepDescription: "This shows if the page is locked for editing. If the page is locked by another user you will be in view only mode.",
            isCompleted: false,
            stepComponentId: "pageLockSwitch",
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        }
    );

    if (!viewOnly) {
        steps.push(
            {
                stepId: 15,
                stepName: "Add New Issue",
                stepDescription: "Click here to add a new issue.",
                isCompleted: false,
                stepComponentId: "addNewIssue",
                displaySide: "left" as "right" | "left" | "top" | "bottom",
            },
            {
                stepId: 16,
                stepName: "Import Issues",
                stepDescription: "Click here to import issues from a CSV file.",
                isCompleted: false,
                stepComponentId: "uploadCSV",
                displaySide: "left" as "right" | "left" | "top" | "bottom",
            }
        );
    }
    return steps;
}

export function getBasicTourSteps(): WizardStep[] {
    return getBasicPageTourViewOnlySteps({ viewOnly: false });
}